import {
  resultOptions,
  useRunAction,
  useTask,
  useUpdateRPASessionStatus,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import { LoadingSpinner } from "@/components/loader";
import { cn } from "@/lib/utils";
import {
  CheckIcon,
  CrossCircledIcon,
  PlayIcon,
  TimerIcon,
} from "@radix-ui/react-icons";
import { RPASessionStatus } from "@repo/shared/rpa";
import { type RPAActionResult, isRPAActionResult } from "@repo/shared/workflow";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { RefreshCcwIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useEffect } from "react";
import type { RunnerProps } from "../shared";

type RHSProps = RunnerProps;

export const HistoryRow: React.FC<{ result: RPAActionResult }> = ({
  result,
}) => {
  const log_history = result.history;
  console.log("log_history", log_history);
  if (!log_history) {
    return null;
  }

  const icon_for_status = {
    completed: (
      <CheckIcon className="text-green-800 bg-green-100 rounded-full p-0.5 w-6 h-6" />
    ),
    failed: (
      <CrossCircledIcon className="text-red-800 bg-red-100 rounded-full p-0.5 w-6 h-6" />
    ),
    in_progress: (
      <div className="border-2 border-blue-500 rounded-full w-6 h-6 flex items-center justify-center">
        <div className="bg-blue-500 rounded-full w-2 h-2 shrink-0" />
      </div>
    ),
    unstarted: (
      <div className="shadow-inner bg-gray-100 rounded-full p-0.5 w-6 h-6" />
    ),
  };

  return (
    <div>
      <h4 className="font-bold text-lg mb-2">RPA Steps</h4>
      <div className="overflow-auto space-y-4">
        {log_history.timeline.map((step, index) => (
          <div key={step.step.id} className="flex">
            <div className="inline-block mr-2">
              {icon_for_status[step.status]}
            </div>
            {/* <div className="font-bold">{step.step.data.actionType}</div> */}
            <div className="flex flex-col mt-0.5">
              <div
                className={cn({
                  "text-gray-500": step.status === "completed",
                  "font-semibold": step.status === "in_progress",
                })}
              >
                {step.step.data.prompt}
              </div>
              {
                <div className="text-sm text-gray-500">
                  {step.current_action}
                </div>
              }
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const useNodeResults = (taskId: string, nodeId: string) => {
  const { data: task } = useTask(taskId);
  const results = useMemo(() => {
    const { run: latestRun } = task?.runs[0] ?? {}; // assume only one run for now
    if (!latestRun) return [];

    return latestRun.results.filter(
      (r) => r.nodeId === nodeId && isRPAActionResult(r),
    );
  }, [task, nodeId]);

  return results;
};

export const Console: React.FC<RunnerProps> = ({ taskId, actionNode }) => {
  const results = useNodeResults(taskId, actionNode.id);
  const resultId = _.last(results)?.id;

  const [debugConnectionURL, setDebugConnectionURL] = useState<{
    resultId: string;
    url: string;
  } | null>(null);

  const { data: result } = useQuery({
    ...resultOptions<RPAActionResult>(taskId, resultId),
    refetchInterval: 2000,
    enabled: !!resultId && !debugConnectionURL?.url,
  });

  useEffect(() => {
    const url = result?.session?.debugUrl;
    if (url) {
      setDebugConnectionURL({ resultId: result.id, url });
    }
  }, [result]);

  return (
    <div className="w-full h-[600px]">
      {/* <pre className="border overflow-auto">
        {JSON.stringify(results, null, 2)}
      </pre> */}
      {debugConnectionURL && (
        <iframe
          title="RPA Console"
          src={debugConnectionURL.url}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
          allow="clipboard-read; clipboard-write"
          className="w-full h-full border"
        />
      )}
    </div>
  );
};

export const RHS: React.FC<RHSProps> = ({ taskId, runId, actionNode }) => {
  const runMut = useRunAction(taskId);
  const results = useNodeResults(taskId, actionNode.id);
  const resultId = _.last(results)?.id;
  const { data: result } = useQuery({
    ...resultOptions<RPAActionResult>(taskId, resultId),
    refetchInterval: 2000,
    enabled: !!resultId,
  });
  const updateSessionStatusMut = useUpdateRPASessionStatus(taskId);
  const sessionStatus = result?.session?.status ?? RPASessionStatus.NOT_RUN;

  return (
    <div className="">
      {/* <pre>{JSON.stringify(result ?? { no: "result", resultId }, null, 2)}</pre> */}
      <div className="flex gap-2">
        <Button
          onClick={async () => {
            try {
              const runResult = await runMut.mutateAsync({
                nodeId: actionNode.id,
                runId,
                resultId: resultId ?? undefined,
              });

              if (!isRPAActionResult(runResult)) {
                return;
              }

              console.log("Run started, setting executionId:", runResult);
              // setExecutionId(runResult.actionExecutionId);
            } catch (e) {
              console.error("Error running action:", e);
            }
          }}
          className={
            sessionStatus === RPASessionStatus.RUNNING
              ? "opacity-50 cursor-not-allowed"
              : ""
          }
          disabled={sessionStatus === RPASessionStatus.RUNNING}
        >
          {sessionStatus === RPASessionStatus.RUNNING ? (
            <div className="mr-2 mt-0.5">
              <LoadingSpinner size="small" />
            </div>
          ) : (
            <PlayIcon className="w-4 h-4 mr-2" />
          )}
          Run
        </Button>
        {sessionStatus === RPASessionStatus.RUNNING && (
          <>
            <Button
              onClick={async () => {
                if (!result) return;

                try {
                  await updateSessionStatusMut.mutateAsync({
                    action: "pause",
                    resultId: result.id,
                  });
                } catch (e) {
                  console.error("Error pausing action:", e);
                }
              }}
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-50"
            >
              <TimerIcon className="w-4 h-4 mr-2" />
              Pause
            </Button>
            <Button
              onClick={async () => {
                if (!result) return;

                // restart
                await runMut.mutateAsync({
                  nodeId: actionNode.id,
                  runId,
                  resultId: result.id,
                });
              }}
              variant="outline"
            >
              <RefreshCcwIcon className="w-4 h-4 mr-2" />
              Restart
            </Button>
          </>
        )}
        {sessionStatus === RPASessionStatus.PAUSED && (
          <Button
            onClick={async () => {
              if (!result) return;

              try {
                await updateSessionStatusMut.mutateAsync({
                  action: "resume",
                  resultId: result.id,
                });
              } catch (e) {
                console.error("Error resuming action:", e);
                // Keep in paused state if resume fails
              }
            }}
            variant="outline"
            className="border-green-500 text-green-500 hover:bg-green-50"
          >
            <PlayIcon className="w-4 h-4 mr-2" />
            Resume
          </Button>
        )}
      </div>
      {result && (
        <div className="mt-8">
          <HistoryRow result={result} />
        </div>
      )}
      {sessionStatus !== RPASessionStatus.NOT_RUN && (
        <div
          className={cn("mt-2 p-4 border rounded-md", {
            "bg-green-100 border-green-300 text-green-700":
              sessionStatus === RPASessionStatus.SUCCESS,
            "bg-orange-100 border-orange-300 text-orange-700":
              sessionStatus === RPASessionStatus.ERROR,
            "bg-yellow-100 border-yellow-300 text-yellow-700":
              sessionStatus === RPASessionStatus.RUNNING,
            "bg-red-100 border-red-300 text-red-700":
              sessionStatus === RPASessionStatus.SYSTEM_ERROR,
            "bg-gray-100 border-gray-300 text-gray-700":
              sessionStatus === RPASessionStatus.PAUSED,
          })}
        >
          {resultId && <p className="text-sm mb-2">Result ID: {resultId}</p>}
          {sessionStatus === "success" && <p>Run successfully.</p>}
          {sessionStatus === "error" && <p>Run failed.</p>}
          {sessionStatus === "system_error" && <p>Something went wrong.</p>}
          {sessionStatus === "running" && <p>Running...</p>}
          {sessionStatus === "paused" && (
            <p>Run paused. Click Resume to continue.</p>
          )}
        </div>
      )}
    </div>
  );
};
